import {
    login,
    profile,
    adminProfile,
    forgotPassword,
    resetPassword,
    passwordlessLogin,
    passwordlessLoginRequest,
} from "../../api/auth";
import {
    getToken,
    setToken,
    removeToken,
    removeUser,
    removeAdminUser,
    getUser,
    getAdminUser,
    setAdminUser,
    setUser,
} from "../../utils/auth";
import { defaultNotificationSettings } from "../../utils/notificationSounds";
import moment from "moment";
import { publishMessage } from "../../utils/systemPubSub";
import { log2 } from "../../utils/log2";
import * as Sentry from "@sentry/vue";
import { checkPermission } from "../../utils/PermissionUtils";

const log = log2("user.vuex");
const pToken = () => window.location.href.indexOf("passwordlessLoginToken") !== -1;
const user = {
    namespaced: true,
    state: {
        user: pToken() ? null : getUser(),
        adminUser: pToken() ? null : getAdminUser(),
        token: pToken() ? null : getToken(),
        timeDiff: 0,
        latency: 0,
        bugExist: false,
    },
    getters: {
        isLoggedIn: (state) => !!state.token && (!!state.user || !!state.adminUser),
        isRoot: (state) => state.adminUser !== undefined || state.user.role.isRoot || false,
        getBugExist: (state) => state.bugExist,
        checkPermission: (state) => (permissionType) => {
            return checkPermission(state.user, permissionType, !!state.adminUser);
        },
    },
    mutations: {
        SET_BUG_EXIST(state, value) {
            state.bugExist = value;
        },
        SET_TOKEN: (state, token) => {
            state.token = token;
        },
        SET_TIME_DIFF_WITH_SERVER_AS_MILLISECONDS: (state, ms) => {
            state.timeDiff = ms;
        },
        SET_LATENCY: (state, ms) => {
            state.latency = ms;
        },
        SET_USER: (state, payload) => {
            state.user = payload;
            removeAdminUser();
            log.debug("SET_USER", { payload });
            if (payload && payload.hasOwnProperty("now")) {
                /*state.timeDiff = moment.duration(moment().diff(moment(payload.now))).asSeconds() * -1;*/
                state.timeDiff = 0;
            }
            if (state.user) {
                if (!state.user.notificationSettings || state.user.notificationSettings.length < 3) {
                    state.user.notificationSettings = defaultNotificationSettings;
                }
            }
            setUser(state.user);
        },
        REFRESH_AUTH_USER: (state, payload) => {
            state.user = payload;
            setUser(payload);
            if (state.user) {
                if (!state.user.notificationSettings || state.user.notificationSettings.length < 3) {
                    state.user.notificationSettings = defaultNotificationSettings;
                }
            }
        },

        SET_ADMIN_USER: (state, payload) => {
            setAdminUser(payload);
            removeUser();
            state.adminUser = payload;
            if (payload && payload.hasOwnProperty("now")) {
                state.timeDiff = moment.duration(moment().diff(moment(payload.now))).asSeconds() * -1;
            }
        },
        SET_STATUS: (state, payload) => {
            state.user.status = payload;
            setUser(state.user);
        },
        UPDATE_LANG: (state, payload) => {
            state.user.lang = payload;
            setUser(state.user);
        },
    },
    actions: {
        login({ commit }, payload) {
            return login(payload.email, payload.password).then((response) => {
                commit("SET_TOKEN", response.data.token);
                setToken(response.data.token);
                const store = require("../../store").default;
                store.dispatch("chat/resetState");
                if (response.data.user) {
                    const user = response.data.user.user;
                    commit("SET_USER", user);
                    commit("SET_ADMIN_USER", null);
                    Sentry.setUser({ email: user.email, name: user.name });
                }

                if (response.data.adminUser) {
                    const user = response.data.adminUser.adminUser;
                    commit("SET_ADMIN_USER", response.data.adminUser.adminUser);
                    commit("SET_USER", null);
                    Sentry.setUser({ email: user.email, name: user.name });
                }
                return Promise.resolve(response.data.user || response.data.adminUser);
            });
        },
        setBugExist({ commit }, value) {
            commit("SET_BUG_EXIST", value);
        },

        passwordlessLogin({ commit }, payload) {
            return passwordlessLogin(payload.email, payload.passwordlessLoginToken).then((response) => {
                commit("SET_TOKEN", response.data.token);
                setToken(response.data.token);

                const store = require("../../store").default;
                store.dispatch("chat/resetState");
                if (response.data.user) {
                    commit("SET_USER", response.data.user.user);
                    commit("SET_ADMIN_USER", null);
                }

                return Promise.resolve(response.data.user);
            });
        },
        forgotPassword({ commit }, payload) {
            return forgotPassword(payload.email).then((response) => {
                return Promise.resolve(response);
            });
        },
        passwordlessLoginRequest({ commit }, email) {
            return passwordlessLoginRequest(email).then((response) => {
                return Promise.resolve(response);
            });
        },
        resetPassword({ commit }, payload) {
            return resetPassword(payload.password, payload.token, payload.isPasswordless).then((response) => {
                if (!payload.isPasswordless) {
                    commit("SET_TOKEN", response.data.token);
                    setToken(response.data.token);
                }
                return Promise.resolve(response);
            });
        },
        profile({ commit }) {
            return profile().then((response) => {
                commit("SET_USER", response.data);
            });
        },
        adminProfile({ commit }) {
            return adminProfile().then((response) => {
                commit("SET_ADMIN_USER", response.data);
            });
        },
        setStatus({ commit }, payload) {
            commit("SET_STATUS", payload);
        },
        setTimeDiffWithServerAsMillisecond({ commit }, ms) {
            commit("SET_TIME_DIFF_WITH_SERVER_AS_MILLISECONDS", ms);
        },
        setLatency({ commit }, ms) {
            commit("SET_LATENCY", ms);
        },
        refreshAuthUser({ commit }, force) {
            return getToken() != null
                ? profile().then((response) => {
                      commit("REFRESH_AUTH_USER", response.data);
                  })
                : Promise.resolve(false);
        },
        logout: function ({ commit }) {
            const isLoggedIn = getToken() != null;
            if (isLoggedIn) {
                commit("SET_TOKEN", null);

                publishMessage("logout", { reconnect: false });

                removeToken();
                removeUser();
                removeAdminUser();
                // commit('SET_USER', null);
                // commit('SET_ADMIN_USER', null);
            }
        },
    },
};

export default user;
